import moment from 'moment';
import { BankType, NegotiationState, OutDebt } from '../../generated/graphql';
import { getNegBooleans } from '../../utils/getNegBooleans';
import thousandSeparator from '../../utils/thousandSeparator';
import { BankCardDynamicData } from '../BankCard/BankCardDynamicData';
import { getNegotiationOptionAlternativeTypeLabel } from '../../utils/getNegotiationOptionAlternativeTypeLabel';
import { ceilToNext } from '../../utils/ceilToNext';

export const getBankCardInfoConfirmation = (
  outDebt: OutDebt,
) => {
  const info: BankCardDynamicData[] = [];
  const {
    hasNeg,
    isConfirmedByUser,
    isExpired,
    isTotalPayment,
    isPaymentPromise,
    isPartialPaymentPromise,
    isRestructure,
    isInstallmentsPlan,
    isCondonation,
    isApprovedByBank,
    negState,
    isBeta,
    isExpressRefinance,
    isRejected,
    isPichincha,
    isPunished,
    isRappiPay,
    isRappiPrestamo,
    isBogota,
    isBogotaTraditional,
    isSentToBank,
    isPrepaymentCompleted,
    isBogotaLowRate,
    isBogotaSpecialCampaign,
    isBogotaSpecialCampaignLowRate,
    isBogotaNoPayment,
    isBalanceBehindPayment,
    isScotiabank,
    isRefinance,
    isJcap,
    isACPK,
    isApex,
    isClaro,
    isTranqui,
    isBogotaTermExtension,
  } = getNegBooleans(outDebt);
  if (!hasNeg) return info;
  const { alternative, extra, updatedAt, negotiationOption } = outDebt.negotiation!;
  const format = 'DD MMM YYYY';
  const add = (label: string, value: string, bold?: boolean) => info.push({ label, value, bold });

  const addDaysBehind = () => {
    if (outDebt?.daysBehind) add('Días de mora', `${outDebt.daysBehind}`);
  };

  const paymentReference = outDebt.extra?.paymentReference;
  const prePayment = alternative.prePayment || 0;
  const paymentDate = extra.info?.maxPaymentDate || negotiationOption.settings?.maxPaymentDate;
  if (isPartialPaymentPromise && [
    NegotiationState.SentToBank,
    NegotiationState.PrepaymentCompleted,
    NegotiationState.ConfirmedByUser,
  ].includes(negState)) {
    const partialPaymentPromiseLabel = outDebt.bank === BankType.Bbva
      ? 'Tipo acuerdo de pago mora'
      : 'Tipo de promesa de pago';
    const alternativeTypeLabel = getNegotiationOptionAlternativeTypeLabel(
      alternative.type!,
      outDebt.bank,
    );
    add(partialPaymentPromiseLabel, alternativeTypeLabel);
    add('Valor de pago', `$${thousandSeparator(alternative.value || 0)}`);
    return info;
  }

  if (isPichincha && isPaymentPromise) {
    if ([ NegotiationState.SentToBank, NegotiationState.PrepaymentCompleted ].includes(negState)) {
      add('Valor de pago mínimo', `$${thousandSeparator(alternative.value || 0)}`);
      return info;
    }
    if (isConfirmedByUser) {
      add('Fecha límite de pago', moment(paymentDate).format(format));
      add('Valor de pago mínimo', `$${thousandSeparator(alternative.value || 0)}`);
      return info;
    }
  }

  if (isBogotaTermExtension) {
    const termExtensionPrePayment = prePayment
      || negotiationOption?.alternatives?.[0]?.prePayment
      || 0;
    if (isConfirmedByUser) {
      add('Fecha límite de pago', moment(paymentDate).format(format));
      add('Valor a pagar', `$ ${thousandSeparator(termExtensionPrePayment || alternative.value)}`, true);
    }
    if (isPrepaymentCompleted || isSentToBank) {
      add('Valor pagado', `$ ${thousandSeparator(termExtensionPrePayment || alternative.value)}`);
    }
    return info;
  }

  if (isBogotaSpecialCampaign && isBalanceBehindPayment) {
    if (paymentReference) info.push({ label: 'Referencia de pago ', value: paymentReference });
    if (isConfirmedByUser) {
      add('Valor a pagar', `$${thousandSeparator(alternative.value || 0)}`);
      add('Fecha límite de pago', moment(paymentDate).format(format));
    }
    if (isPrepaymentCompleted || isApprovedByBank || isSentToBank) {
      add('Valor pagado', `$${thousandSeparator(alternative.value || 0)}`);
    }
    if (isBogotaSpecialCampaignLowRate && alternative.effectiveAnnualRate != null) {
      add('Tasa de interés', `${(alternative.effectiveAnnualRate * 100).toFixed(2)}% EA`);
    }
    return info;
  }

  if (isApex) {
    if (isConfirmedByUser) {
      if (outDebt?.extra?.movistarInvoice) add('Tienes factura Apex', outDebt?.extra?.movistarInvoice);
      addDaysBehind();
      add('Valor a pagar', `$${thousandSeparator(alternative.value || 0)}`);
      return info;
    }
    if (isSentToBank) {
      add('Pago recibido', moment(updatedAt).format(format));
      add('Valor pagado', `$${thousandSeparator(alternative.value || 0)}`);
      return info;
    }
    if (isRejected) {
      add('Saldo total', `$ ${thousandSeparator(outDebt.balancePrincipal)}`);
      if (outDebt.paymentDate) add('Fecha límite de pago', moment(outDebt.paymentDate).format(format));
      if (outDebt?.balanceBehind) add('Pago Mínimo', `$ ${thousandSeparator(ceilToNext(outDebt.balanceBehind, 10000))}`);
      return info;
    }
  }

  if ((isClaro || isTranqui) && !isBogotaTermExtension) {
    if (paymentReference) info.push({ label: 'Referencia de pago ', value: paymentReference });
    if (isConfirmedByUser) {
      add('Valor a pagar', `$${thousandSeparator(alternative.value || 0)}`);
    }
    if (isSentToBank || isPrepaymentCompleted) {
      add('Valor pagado', `$${thousandSeparator(alternative.value || 0)}`);
    }
    return info;
  }

  if (isBeta) {
    if (outDebt?.subType) add('Producto', outDebt?.subType);
  }

  if (isExpressRefinance && isRejected && isPichincha) {
    add('Fecha límite de pago', moment(outDebt.paymentDate).format(format));
    if (outDebt?.minPayment) add('Cuota actual', `$ ${thousandSeparator(outDebt.minPayment)}`);
    if (outDebt?.daysBehind) add('Mora', `${outDebt.daysBehind} días`);
    return info;
  }

  const { negotiationOption: { alternatives: [ { balance: negBalance } ] } } = outDebt.negotiation!;
  if (isBogota && !isInstallmentsPlan && !isRefinance && negBalance) add('Saldo total', `$ ${thousandSeparator(negBalance)}`);

  if (isRejected && !isPunished) {
    if (outDebt.paymentDate && !isRappiPay && !isRappiPrestamo) add('Fecha límite de pago', moment(outDebt.paymentDate).format(format));
    if (outDebt?.minPayment) add('Pago Mínimo', `$ ${thousandSeparator(outDebt.minPayment)}`);
    if (outDebt?.daysBehind) add('Mora', `${outDebt.daysBehind} días`);
    return info;
  }

  const newPaymentDay = extra.info?.newPaymentDay || moment(outDebt.paymentDate).date();

  if (isBogota && isBalanceBehindPayment) {
    if (isBogotaTraditional) {
      if (isConfirmedByUser) {
        add('Valor a pagar', `$${thousandSeparator(alternative.value || 0)}`);
        add('Fecha límite de pago', moment(paymentDate).format(format));
      }
      if (isPrepaymentCompleted || isApprovedByBank || isSentToBank) {
        add('Valor pagado', `$${thousandSeparator(alternative.value || 0)}`);
      }
    }
    if (isBogotaLowRate) {
      if (outDebt.extra?.popupRate) add('Reducción de la Tasa Efectiva Anual al', `${outDebt.extra.popupRate}%`);
    }
    if (isBogotaNoPayment) {
      addDaysBehind();
      if (isRejected) {
        add('Saldo en mora', `$${thousandSeparator(outDebt.minPayment || 0)}`);
      } else {
        add('Valor abono sugerido', `$${thousandSeparator(alternative.value || 0)}`);
      }
    }
    return info;
  }

  if ((isExpired || isConfirmedByUser) && (prePayment > 0 || isTotalPayment || isCondonation)) {
    add('Fecha límite de pago', moment(paymentDate).format(format), isPartialPaymentPromise);
    if (isExpressRefinance) {
      add('Abono inicial', `$ ${thousandSeparator(prePayment || alternative.value)}`);
    } else {
      add('Valor a pagar', `$ ${thousandSeparator(prePayment || alternative.value)}`, true);
    }
    return info;
  }

  if (isTotalPayment) {
    add('Pago recibido', moment(updatedAt).format(format));
    add('Valor pagado', `$${thousandSeparator(alternative.value || 0)}`);
    return info;
  }

  if (isRestructure || isInstallmentsPlan) {
    if (isBogota) addDaysBehind();
    add('Cuota solicitada aprox.', `$${thousandSeparator(alternative.value || 0)}`);
    return info;
  }

  if (newPaymentDay) add('Fecha de pago', `${newPaymentDay} de cada mes`);
  if (isPaymentPromise) {
    add('Valor aprox.', `$${thousandSeparator(alternative.value || 0)}`);
  } else if (isExpressRefinance) {
    add('Nueva cuota aprox', `$${thousandSeparator(alternative.value || 0)}`);
  } else {
    let valueText = 'Cuota solicitada aprox.';
    if (isCondonation) valueText = isApprovedByBank ? 'Valor pagado' : 'Valor pago';
    add(valueText, `$${thousandSeparator(alternative.value || 0)}`);
  }
  if (isJcap && isACPK) add('Nueva fecha de pago', `${moment(negotiationOption.settings?.maxPaymentDate).date()} de cada mes`);
  if (isRefinance && isScotiabank) {
    if (outDebt?.balance) add('Saldo pendiente', `$${thousandSeparator(outDebt.balance)}`);
    addDaysBehind();
  }
  return info;
};
